import { AfterViewInit, Component, ElementRef, Input, OnInit, QueryList, ViewChildren } from '@angular/core';

import { DTO } from '../../../app/core/models/dto';
import { PromotionLeaderboardService } from '../../core/service/promotion-leaderboard.service';
import { CommonService } from '../../core/service/common.service';
import { MyInfo, PrizePoolList, RankingPlayerList, WsopNationalityRankingItem } from '../../core/interface/leaderboard.interface';
import { environment } from '../../../environments/environment';
import { iocCountryMap } from '../../shared/const';

@Component({
    selector: 'app-promotion-leaderboard-view',
    templateUrl: 'promotion-leaderboard-view.component.html',
    styleUrls: ['promotion-leaderboard-view.component.scss']
})
export class PromotionLeaderboardViewComponent implements OnInit, AfterViewInit {
    @Input() public type: DTO.Enums.PromotionType;
    @Input() public promotionId: number;
    @Input() public nickname: string;
    @Input() public brandId: string;
    @Input() public siteId: string;
    @Input() public playerLimit: number;
    @Input() public prizeVisibility: boolean;
    @Input() public onlyCoin: boolean;
    @Input() public hideSymbol: boolean;
    @Input() public hideCustomSymbol: boolean;
    @Input() public hideRank: boolean;
    @Input() public focusFirstPlayer: boolean;
    @Input() public optInGroupName: string;
    @Input() public leaderboardType: DTO.Enums.LeaderboardType;
    @Input() public backgroundColor: string;
    @ViewChildren('ranks') myRankMarking: QueryList<ElementRef>;
    @ViewChildren('runningPrizeLevel') runningPrizeLevelMarking: QueryList<ElementRef>;
    public progress = false;
    public isMaintenance = false;
    public isScrolling = false;
    public myInfo: MyInfo;
    public prizePoolList: PrizePoolList[];
    public rankingList: RankingPlayerList[];
    public wsopNationalityRankingList: WsopNationalityRankingItem[];
    public isBountyKOPromotion = false;
    public isRefreshable = true;
    public countryCodes: { [key: string]: string } = {};
    public leaderboardTypes = DTO.Enums.LeaderboardType;
    private environment = environment.env;
    private countryFlagMap = new Map<string, any>([
        ['UA', { RU: 'RU_WB'}],
        ['CN', { TW: 'CN' }],
        ['LB', { IL: 'CY' }]
    ]);
    private wsopCountryFlagMap = new Map<string, any>([
        ['UA', { RU: 'RU_WB'}],
        ['LB', { IL: 'IOC' }],
    ]);
    public countryCode: string;

    private wsopPrizeCustomList = {
        PROD: [43692],
        TEST: [27406]
    };

    public get isMobile() {
        return navigator.userAgent.match(/Mobile|iP(hone|od)|BlackBerry|IEMobile|Kindle|NetFront|Silk-Accelerated|(hpw|web)OS|Fennec|Minimo|Opera M(obi|ini)|Blazer|Dolfin|Dolphin|Skyfire|Zune/);
    }

    public get isWsopPrizeCustom() {
        const env = this.environment !== 'PROD' ? 'TEST' : this.environment;
        return this.wsopPrizeCustomList[env].indexOf(Number(this.promotionId)) > -1;
    }

    public get isChase() {
        return [DTO.Enums.PromotionType.CHASE, DTO.Enums.PromotionType.TOURNAMENT_LEADERBOARD_CHASE].indexOf(this.type) !== -1;
    }

    public get isTournamentLeaderBoard() {
        return this.type === DTO.Enums.PromotionType.TOURNAMENT_LEADERBOARD;
    }

    constructor(
        private leaderboardService: PromotionLeaderboardService,
        private commonService: CommonService) {
    }

    public async ngOnInit() {
        await this.load();
    }

    public ngAfterViewInit() {
        this.listScrollToView(this.myRankMarking);
        this.listScrollToView(this.runningPrizeLevelMarking);
    }

    private listScrollToView(scrollTargetElements: QueryList<ElementRef>) {
        scrollTargetElements.changes.subscribe(() => {
            scrollTargetElements.toArray().forEach(el => {
                if (el.nativeElement.classList.contains('myRank')) {
                    el.nativeElement.scrollIntoView({ behavior: 'smooth' });
                }
            });
        });
    }

    private async load() {
        this.progress = true;

        await this.checkMaintenance();
        if (this.isMaintenance) {
            this.progress = false;
            return;
        }

        const promises = [this.getCountryCodes()];

        if (DTO.Enums.LeaderboardType.WSOP === this.leaderboardType) {
            promises.push(this.getWsopNationalityRankingList());
        } else {
            const playersLimit = await this.getNumberOfPlayers();

            if (this.hideRank) {
                promises.push(this.getPlayerRankingList(playersLimit));
            } else {
                promises.push(...[
                    this.getMyInfo(),
                    this.getPlayerRankingList(playersLimit)
                ]);
            }
        }

        if (this.isChase) {
            promises.push(this.getPrizePoolList());
        }

        await Promise.all(promises);
        this.countryCode = this.leaderboardService.countryCode;
        this.progress = false;
    }

    private async checkMaintenance() {
        try {
            const maintenance = await this.commonService.getMaintenance();
            this.isMaintenance = maintenance !== 'OK';
        } catch (e) {
            this.isMaintenance = true;
            console.error(e);
        }
    }

    private async getCountryCodes() {
        try {
            this.countryCodes = await this.commonService.getCountryCodes();
        } catch (e) {
            console.error(e);
        }
    }

    private async getMyInfo() {
        try {
            if (!this.nickname) {
                this.myInfo = null;
                return;
            }
            const targetParams = {
                promotionId: this.promotionId,
                nickname: this.nickname,
                brandId: this.brandId,
                siteId: this.siteId
            };

            this.myInfo = await this.leaderboardService.getMyInfo(targetParams, !this.prizeVisibility);
        } catch (e) {
            this.myInfo = null;
            console.error(e);
        }
    }

    private async getWsopNationalityRankingList() {
        try {
            const result = await this.leaderboardService.getWsopNationalityLeaderboard(this.optInGroupName, 100);

            this.wsopNationalityRankingList = result.map(item => ({
                ...item,
                displayCountryId: this.getDisplayCountryCode(this.wsopCountryFlagMap, item.nationality),
                displayIocCountryId: iocCountryMap.has(item.nationality) ? iocCountryMap.get(item.nationality) : item.nationality,
                displayPrize: this.getDisplayPrizeForWsopNationalityRanking(item.prize)
            }));

            this.isScrolling = this.wsopNationalityRankingList.length > 10 && !this.isMobile;
        } catch (e) {
            console.log('exception!!', e);
            this.wsopNationalityRankingList = [];
        }
    }

    private getDisplayPrizeForWsopNationalityRanking(prizeValue: string) {
        if (!prizeValue) {
            return '-';
        }

        const prize = prizeValue.split('|');
        if (this.onlyCoin) {
            return 1 < prize.length ? prize[1] : prize[0];
        } else {
            return prize[0];
        }
    }

    private async getPlayerRankingList(limit: number) {
        try {
            const ret = await this.leaderboardService.getRankingPlayerList({
                promotionId: this.promotionId,
                nickname: this.nickname
            }, limit, !this.prizeVisibility);

            this.rankingList = (this.isTournamentLeaderBoard ? ret : ret.filter(row => row.isCorrect)).map(row => ({
                ...row,
                displayCountryId: this.getDisplayCountryCode(this.countryFlagMap, row.countryId)
            }));

            this.isScrolling = this.rankingList.length > 10 && !this.isMobile;
        } catch (e) {
            this.rankingList = [];
        }
    }

    private getDisplayCountryCode(countryFlagMap: Map<string, any>, countryId: string) {
        if (!countryFlagMap.has(this.leaderboardService.countryCode)) {
            return countryId;
        }

        const flagMap = countryFlagMap.get(this.leaderboardService.countryCode);
        return Object.prototype.hasOwnProperty.call(flagMap, countryId) ? flagMap[countryId] : countryId;
    }

    private async getPrizePoolList(limit: number = null) {
        try {
            const prizePoolList = await this.leaderboardService.getPrizePoolList({ promotionId: this.promotionId });
            const selectedPrizePool = prizePoolList.find(prizePool => prizePool.winners !== prizePool.maxWinners) || prizePoolList[prizePoolList.length - 1];
            if (!!selectedPrizePool) {
                selectedPrizePool.isRunning = true;
            }
            this.prizePoolList = prizePoolList;

            const selectedLevel = selectedPrizePool.level;
            const rankingLimit = (
                (
                    selectedLevel === 1 ?
                        selectedPrizePool
                        : prizePoolList.find(prizePool => prizePool.level === selectedPrizePool.level - 1)
                ) || {} as PrizePoolList
            ).maxWinners || 0;

            await this.getPlayerRankingList(limit ? limit : Math.max(rankingLimit, 10));
        } catch (e) {
            this.prizePoolList = [];
            console.error(e);
        }
    }

    private async getNumberOfPlayers(): Promise<number> {
        try {
            const promotionDetail = await this.leaderboardService.getPromotionType(this.promotionId);
            if (!promotionDetail['description']) {
                return 250;
            }
            const description = JSON.parse(promotionDetail['description']);
            this.type = promotionDetail.type;
            this.isBountyKOPromotion = promotionDetail['isBountyKOPromotion'] ? promotionDetail['isBountyKOPromotion'] : false;
            return !!description.displayedPlayers ? description.displayedPlayers : 250;
        } catch (e) {
            console.error(e);
            return 250;
        }
    }

    public async refresh(event: Event) {
        event.preventDefault();

        let requestTimer = null;
        const DELAY_SECOND = 5000;

        requestTimer = setTimeout(() => {
            this.isRefreshable = true;
            clearTimeout(requestTimer);
        }, DELAY_SECOND);

        await this.load();
        this.isRefreshable = false;
    }
}





















